import React, { useState } from "react";
import { researchExperience } from "../data/portfolioData";

const ResearchExperience: React.FC = () => {
  const [expandedResearch, setExpandedResearch] = useState<string | null>(null);

  const toggleResearchExpansion = (researchId: string) => {
    setExpandedResearch(expandedResearch === researchId ? null : researchId);
  };

  const getMediaIcon = (type: string) => {
    switch (type) {
      case "video":
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
          </svg>
        );
      case "github":
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z"
              clipRule="evenodd"
            />
          </svg>
        );
      case "paper":
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
              clipRule="evenodd"
            />
          </svg>
        );
      case "presentation":
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
              clipRule="evenodd"
            />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        );
    }
  };

  return (
    <section id="research" className="section-padding">
      <div className="container-max">
        <div className="text-center mb-16">
          <h2 className="section-title">Research Experience</h2>
          <p className="section-subtitle">
            Academic research projects and contributions to the field
          </p>
        </div>

        <div className="space-y-8">
          {researchExperience.map((research) => (
            <div key={research.id} className="card card-hover">
              {/* Research Header */}
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
                <div className="flex-1">
                  <div className="flex items-center mb-3">
                    <h3 className="text-2xl font-bold text-white mr-4">
                      {research.title}
                    </h3>
                    <span className="text-sm text-primary-400 font-medium bg-primary-500/20 px-3 py-1 rounded-full border border-primary-500/30">
                      {research.duration}
                    </span>
                  </div>

                  <div className="flex items-center text-dark-300 mb-4">
                    <svg
                      className="w-5 h-5 mr-3 text-primary-400"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="font-semibold text-white">
                      {research.institution}
                    </span>
                    {research.location && (
                      <>
                        <span className="mx-3 text-primary-400">•</span>
                        <span>{research.location}</span>
                      </>
                    )}
                  </div>

                  {research.role && (
                    <div className="flex items-center text-dark-300 mb-4">
                      <svg
                        className="w-5 h-5 mr-3 text-primary-400"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="font-semibold text-white">
                        {research.role}
                      </span>
                    </div>
                  )}
                </div>

                {/* Media Links */}
                {research.links && research.links.length > 0 && (
                  <div className="flex gap-2 mb-4 lg:mb-0">
                    {research.links.map((link, index) => (
                      <a
                        key={index}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-secondary inline-flex items-center text-sm px-3 py-2"
                      >
                        {getMediaIcon(link.type)}
                        <span className="ml-2">{link.label}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* Research Description */}
              <p className="text-dark-300 mb-6 leading-relaxed text-lg">
                {research.description}
              </p>

              {/* Technologies */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-white mb-3">
                  Technologies Used
                </h4>
                <div className="flex flex-wrap gap-3">
                  {research.technologies.map((tech, index) => (
                    <span key={index} className="tech-tag">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              {/* Outcomes */}
              {research.outcomes && research.outcomes.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-white mb-3">
                    Key Outcomes
                  </h4>
                  <ul className="space-y-2">
                    {research.outcomes.map((outcome, index) => (
                      <li
                        key={index}
                        className="flex items-start text-dark-300"
                      >
                        <svg
                          className="w-5 h-5 mr-3 mt-0.5 text-primary-400 flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {outcome}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Expandable Projects Section */}
              {research.projects && research.projects.length > 0 && (
                <div>
                  <button
                    onClick={() => toggleResearchExpansion(research.id)}
                    className="flex items-center text-primary-400 hover:text-primary-300 transition-colors mb-4"
                  >
                    <span className="font-semibold">
                      {expandedResearch === research.id ? "Hide" : "View"}{" "}
                      Research Projects ({research.projects.length})
                    </span>
                    <svg
                      className={`w-5 h-5 ml-2 transition-transform ${
                        expandedResearch === research.id ? "rotate-180" : ""
                      }`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>

                  {expandedResearch === research.id && (
                    <div className="space-y-6 pt-4 border-t border-dark-600">
                      {research.projects.map((project) => (
                        <div
                          key={project.id}
                          className="bg-dark-700/50 rounded-xl p-6 border border-dark-600"
                        >
                          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4">
                            <h5 className="text-xl font-semibold text-white mb-2 lg:mb-0">
                              {project.title}
                            </h5>
                            <div className="flex gap-2">
                              {project.links &&
                                project.links.map((link, index) => (
                                  <a
                                    key={index}
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-secondary inline-flex items-center text-sm px-3 py-2"
                                  >
                                    {getMediaIcon(link.type)}
                                    <span className="ml-2">{link.label}</span>
                                  </a>
                                ))}
                            </div>
                          </div>

                          <p className="text-dark-300 mb-4 leading-relaxed">
                            {project.description}
                          </p>

                          {/* Project Technologies */}
                          {project.technologies &&
                            project.technologies.length > 0 && (
                              <div className="mb-4">
                                <h6 className="text-sm font-semibold text-white mb-2">
                                  Technologies
                                </h6>
                                <div className="flex flex-wrap gap-2">
                                  {project.technologies.map((tech, index) => (
                                    <span
                                      key={index}
                                      className="tech-tag text-xs"
                                    >
                                      {tech}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                          {/* Project Outcomes */}
                          {project.outcomes && project.outcomes.length > 0 && (
                            <div>
                              <h6 className="text-sm font-semibold text-white mb-2">
                                Outcomes
                              </h6>
                              <ul className="space-y-1">
                                {project.outcomes.map((outcome, index) => (
                                  <li
                                    key={index}
                                    className="flex items-start text-dark-300 text-sm"
                                  >
                                    <svg
                                      className="w-4 h-4 mr-2 mt-0.5 text-primary-400 flex-shrink-0"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                    {outcome}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Call to action for more research */}
        <div className="text-center mt-16">
          <p className="text-dark-300 mb-6 text-lg">
            Interested in collaborating on research projects?
          </p>
          <a href="#contact" className="btn-accent inline-flex items-center">
            <svg
              className="w-5 h-5 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
              <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
            </svg>
            Get in Touch
          </a>
        </div>
      </div>
    </section>
  );
};

export default ResearchExperience;
